import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  createTimeCorrectionRequest,
  getTimeCorrectionRequestById,
  approveTimeCorrectionRequest,
  rejectTimeCorrectionRequest,
  getMyTimeCorrections,
  getTimeCorrectionsWorkspace,
} from '@/api/services/timeCorrection.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { attendanceKeys } from '@/features/attendance/hooks/useAttendance'
import { invalidateDashboards } from '@/features/dashboard/hooks/useDashboardStats'
import type {
  MyTimeCorrectionRequestListQuery,
  CreateTimeCorrectionRequestDto,
  RejectTimeCorrectionRequestDto,
  ApproveTimeCorrectionRequestDto,
} from '@/features/attendance/types/timeCorrection.types'

export const timeCorrectionKeys = {
  root: ['timeCorrection'] as const,
  all: (companyId: string | null) => ['timeCorrection', companyId] as const,
  detail: (companyId: string | null, requestId: string) =>
    ['timeCorrection', companyId, 'detail', requestId] as const,
  mine: (params: MyTimeCorrectionRequestListQuery | undefined) =>
    ['timeCorrection', 'me', params] as const,
  workspace: (params: MyTimeCorrectionRequestListQuery | undefined) =>
    ['timeCorrection', 'workspace', params] as const,
}

export function useMyTimeCorrections(
  params: MyTimeCorrectionRequestListQuery | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: timeCorrectionKeys.mine(params),
    queryFn: () => getMyTimeCorrections(params),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useTimeCorrectionsWorkspace(
  params: MyTimeCorrectionRequestListQuery | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: timeCorrectionKeys.workspace(params),
    queryFn: () => getTimeCorrectionsWorkspace(params),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useTimeCorrectionRequestById(
  companyId: string | null,
  requestId: string | null
) {
  return useQuery({
    queryKey: timeCorrectionKeys.detail(companyId, requestId ?? ''),
    queryFn: () => getTimeCorrectionRequestById(companyId!, requestId!),
    enabled: !!companyId && !!requestId,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useCreateTimeCorrectionRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      body,
    }: {
      companyId: string
      body: CreateTimeCorrectionRequestDto
    }) => createTimeCorrectionRequest(companyId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: timeCorrectionKeys.root })
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.all(variables.companyId),
      })
      void invalidateDashboards(queryClient)
      toast.success('Time correction request submitted.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useApproveTimeCorrectionRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      requestId,
      body,
    }: {
      companyId: string
      requestId: string
      body?: ApproveTimeCorrectionRequestDto
    }) => approveTimeCorrectionRequest(companyId, requestId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: timeCorrectionKeys.root })
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.all(variables.companyId),
      })
      void invalidateDashboards(queryClient)
      toast.success(
        variables.body?.approvedClockIn || variables.body?.approvedClockOut
          ? 'Time correction approved with your adjusted times.'
          : 'Time correction request approved.'
      )
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
      // The request may have been decided elsewhere (e.g. "already approved") —
      // refetch so the stale pending row disappears from lists and the dashboard.
      queryClient.invalidateQueries({ queryKey: timeCorrectionKeys.root })
      void invalidateDashboards(queryClient)
    },
  })
}

export function useRejectTimeCorrectionRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      requestId,
      body,
    }: {
      companyId: string
      requestId: string
      body: RejectTimeCorrectionRequestDto
    }) => rejectTimeCorrectionRequest(companyId, requestId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: timeCorrectionKeys.root })
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.all(variables.companyId),
      })
      void invalidateDashboards(queryClient)
      toast.success('Time correction request rejected.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
      // The request may have been decided elsewhere (e.g. already approved/rejected) —
      // refetch so the stale pending row disappears from lists and the dashboard.
      queryClient.invalidateQueries({ queryKey: timeCorrectionKeys.root })
      void invalidateDashboards(queryClient)
    },
  })
}
